import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../core/service/language.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';

interface PublicSchool {
  id: number;
  school_name: string;
  type: string;
  description?: string;
  logo?: string;
  available_capacity: number;
}

@Component({
  selector: 'app-landing',
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss'],
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    NgClass,
    RouterLink,
    TranslateModule,
    FormsModule,
  ],
})
export class LandingComponent implements OnInit, OnDestroy {
  Math = Math;
  currentYear = new Date().getFullYear();
  mobileNavOpen = false;

  listLang = [
    { text: 'English', flag: 'assets/images/flags/us.svg', lang: 'en' },
    { text: 'Français', flag: 'assets/images/flags/france.svg', lang: 'fr' },
    { text: 'العربية', flag: 'assets/images/flags/TN.svg', lang: 'ar' },
  ];
  currentLang = 'en';
  currentFlag = 'assets/images/flags/us.svg';

  features = [
    { icon: 'school', titleKey: 'LANDING.FEATURES.MANAGE_TITLE', descKey: 'LANDING.FEATURES.MANAGE_DESC', color: '#27ae60' },
    { icon: 'family_restroom', titleKey: 'LANDING.FEATURES.PARENTS_TITLE', descKey: 'LANDING.FEATURES.PARENTS_DESC', color: '#1a7a42' },
    { icon: 'calendar_month', titleKey: 'LANDING.FEATURES.CALENDAR_TITLE', descKey: 'LANDING.FEATURES.CALENDAR_DESC', color: '#f39c12' },
    { icon: 'group', titleKey: 'LANDING.FEATURES.TEACHERS_TITLE', descKey: 'LANDING.FEATURES.TEACHERS_DESC', color: '#e74c3c' },
    { icon: 'security', titleKey: 'LANDING.FEATURES.SECURITY_TITLE', descKey: 'LANDING.FEATURES.SECURITY_DESC', color: '#00bcd4' },
  ];

  steps = [
    { number: '1', titleKey: 'LANDING.STEPS.SEARCH_TITLE', descKey: 'LANDING.STEPS.SEARCH_DESC', icon: 'search' },
    { number: '2', titleKey: 'LANDING.STEPS.REGISTER_TITLE', descKey: 'LANDING.STEPS.REGISTER_DESC', icon: 'app_registration' },
    { number: '3', titleKey: 'LANDING.STEPS.CONFIRM_TITLE', descKey: 'LANDING.STEPS.CONFIRM_DESC', icon: 'verified' },
    { number: '4', titleKey: 'LANDING.STEPS.START_TITLE', descKey: 'LANDING.STEPS.START_DESC', icon: 'celebration' },
  ];

  galleryImages = [
    { src: 'assets/images/home/home_image1.jpg', alt: 'Kids playing' },
    { src: 'assets/images/home/home_image2.jpg', alt: 'Children learning' },
    { src: 'assets/images/home/home_image3.jpg', alt: 'Happy classroom' },
    { src: 'assets/images/home/home_image4.jpg', alt: 'Kids activities' },
    { src: 'assets/images/home/home_image5.jpg', alt: 'Kindergarten fun' },
    { src: 'assets/images/home/home_image6.jpg', alt: 'Kids together' },
    { src: 'assets/images/home/home_image7.jpg', alt: 'Learning time' },
    { src: 'assets/images/home/home_image8.jpg', alt: 'Creative play' },
  ];

  registeredSchools: PublicSchool[] = [];
  loadingSchools = true;
  activeSchoolIndex = 0;
  private autoSlideInterval: any;

  schoolSignup = {
    schoolName: '',
    type: '',
    city: '',
    contactName: '',
    email: '',
    phone: '',
    message: '',
  };
  schoolSignupSending = false;
  schoolSignupSent = false;

  constructor(
    public router: Router,
    private translate: TranslateService,
    private languageService: LanguageService,
    private http: HttpClient,
  ) {
    this.currentLang = localStorage.getItem('lang') || 'fr';
    const found = this.listLang.find(l => l.lang === this.currentLang);
    if (found) {
      this.currentFlag = found.flag;
    }
  }

  ngOnInit(): void {
    this.http.get<PublicSchool[]>(`${environment.apiUrl}/api/public/schools`).subscribe({
      next: (schools) => {
        this.registeredSchools = schools;
        this.loadingSchools = false;
        this.startAutoSlide();
      },
      error: () => {
        this.loadingSchools = false;
      },
    });
  }

  toggleMobileNav(): void {
    this.mobileNavOpen = !this.mobileNavOpen;
  }

  closeMobileNav(): void {
    this.mobileNavOpen = false;
  }

  submitSchoolSignup(): void {
    if (this.schoolSignupSending) return;
    this.schoolSignupSending = true;
    const payload = { ...this.schoolSignup, source: 'landing-school-signup' };
    this.http.post(`${environment.apiUrl}/api/public/school-leads`, payload).subscribe({
      next: () => {
        this.schoolSignupSending = false;
        this.schoolSignupSent = true;
      },
      error: () => {
        const subject = encodeURIComponent(`Demande établissement — ${this.schoolSignup.schoolName}`);
        const body = encodeURIComponent(
          `Établissement: ${this.schoolSignup.schoolName}\nType: ${this.schoolSignup.type}\nVille: ${this.schoolSignup.city}\nContact: ${this.schoolSignup.contactName}\nEmail: ${this.schoolSignup.email}\nTél: ${this.schoolSignup.phone}\n\n${this.schoolSignup.message}`
        );
        window.location.href = `mailto:contact@gardekids.com?subject=${subject}&body=${body}`;
        this.schoolSignupSending = false;
        this.schoolSignupSent = true;
      },
    });
  }

  prevSchool(): void {
    const len = this.registeredSchools.length;
    this.activeSchoolIndex = (this.activeSchoolIndex - 1 + len) % len;
    this.restartAutoSlide();
  }

  nextSchool(): void {
    this.activeSchoolIndex = (this.activeSchoolIndex + 1) % this.registeredSchools.length;
    this.restartAutoSlide();
  }

  goToSchool(index: number): void {
    this.activeSchoolIndex = index;
    this.restartAutoSlide();
  }

  getCardPosition(index: number): number {
    const len = this.registeredSchools.length;
    let diff = index - this.activeSchoolIndex;
    if (diff > len / 2) diff -= len;
    if (diff < -len / 2) diff += len;
    return diff;
  }

  private startAutoSlide(): void {
    if (this.registeredSchools.length <= 1) return;
    this.autoSlideInterval = setInterval(() => {
      this.activeSchoolIndex = (this.activeSchoolIndex + 1) % this.registeredSchools.length;
    }, 4000);
  }

  private restartAutoSlide(): void {
    clearInterval(this.autoSlideInterval);
    this.startAutoSlide();
  }

  ngOnDestroy(): void {
    clearInterval(this.autoSlideInterval);
  }

  get logoSrc(): string {
    return this.currentLang === 'ar'
      ? 'assets/images/logos/logo-gardKids-ar-png.png'
      : 'assets/images/logos/logo-gardKids-fr-png.png';
  }

  setLanguage(item: { text: string; flag: string; lang: string }) {
    this.currentLang = item.lang;
    this.currentFlag = item.flag;
    this.languageService.setLanguage(item.lang);
  }

  scrollTo(sectionId: string) {
    this.closeMobileNav();
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }
}
